import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import {
  Bell,
  BellOff,
  Briefcase,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  FileText,
  Calendar,
  ClipboardList,
  MessageSquare,
  CheckCheck
} from 'lucide-react';

const TYPE_ICONS = {
  task_assigned: { icon: Briefcase, className: 'bg-[#1F3D2B]/10 text-[#1F3D2B]' },
  task_updated: { icon: Briefcase, className: 'bg-amber-100 text-amber-700' },
  entry_needs_fix: { icon: AlertTriangle, className: 'bg-red-100 text-red-700' },
  entry_approved: { icon: CheckCircle, className: 'bg-green-100 text-green-700' },
  deadline_soon: { icon: Clock, className: 'bg-amber-100 text-amber-700' },
  deadline_overdue: { icon: Clock, className: 'bg-red-100 text-red-700' },
  job_status: { icon: TrendingUp, className: 'bg-blue-100 text-blue-700' },
  project_status: { icon: TrendingUp, className: 'bg-teal-100 text-teal-700' },
  project_next_step: { icon: Calendar, className: 'bg-indigo-100 text-indigo-700' },
  document_new: { icon: FileText, className: 'bg-blue-100 text-blue-700' },
  entry_submitted: { icon: ClipboardList, className: 'bg-[#1F3D2B]/10 text-[#1F3D2B]' },
  task_status: { icon: ClipboardList, className: 'bg-indigo-100 text-indigo-700' },
  task_report: { icon: ClipboardList, className: 'bg-amber-100 text-amber-700' },
  client_message: { icon: MessageSquare, className: 'bg-blue-100 text-blue-700' },
  message_new: { icon: MessageSquare, className: 'bg-blue-100 text-blue-700' }
};

const relativeTime = (value) => {
  const diff = Date.now() - new Date(value).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'tikko';
  if (minutes < 60) return `pirms ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `pirms ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `pirms ${days} d.`;
  return new Date(value).toLocaleDateString('lv-LV');
};

const NotificationItem = ({ item, onOpen }) => {
  const config = TYPE_ICONS[item.type] || { icon: Bell, className: 'bg-gray-100 text-gray-600' };
  const Icon = config.icon;
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className={`w-full text-left flex gap-3 px-4 py-3 transition-colors hover:bg-[#1F3D2B]/5 ${item.is_read ? 'opacity-70' : 'bg-[#f3f7f3]'}`}
      data-testid={`notification-item-${item.id}`}
    >
      <span className={`mt-0.5 w-8 h-8 shrink-0 rounded-full flex items-center justify-center ${config.className}`}>
        <Icon className="w-4 h-4" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="flex items-start justify-between gap-2">
          <span className={`text-sm leading-snug ${item.is_read ? 'font-medium' : 'font-semibold'} text-[#2E2E2E]`} data-testid={`notification-title-${item.id}`}>{item.title}</span>
          {!item.is_read && <span className="mt-1.5 w-2 h-2 rounded-full bg-[#1F3D2B] shrink-0" data-testid={`notification-unread-dot-${item.id}`} />}
        </span>
        {item.body && <span className="block text-xs text-[#2E2E2E]/70 mt-0.5 whitespace-pre-line line-clamp-2">{item.body}</span>}
        <span className="block text-[11px] text-[#2E2E2E]/50 mt-1">{relativeTime(item.created_at)}</span>
      </span>
    </button>
  );
};

export const NotificationBell = ({ items, unread, markRead, markAll, className = '', idSuffix = '', side = 'bottom', align = 'end' }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const tid = (base) => `${base}${idSuffix}`;

  const openItem = async (item) => {
    if (!item.is_read) markRead(item.id);
    setOpen(false);
    if (item.link) navigate(item.link);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`relative p-2 rounded-lg hover:bg-[#1F3D2B]/5 transition-colors ${className}`}
          aria-label="Paziņojumi"
          data-testid={tid('notification-bell')}
        >
          <Bell className="w-6 h-6 text-[#1F3D2B]" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center animate-fade-in" data-testid={tid('notification-unread-count')}>
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent side={side} align={align} sideOffset={8} className="w-[360px] max-w-[92vw] p-0 overflow-hidden shadow-xl border-[#1F3D2B]/10" data-testid={tid('notification-panel')}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white">
          <div>
            <p className="font-semibold text-[#1F3D2B]" data-testid={tid('notification-panel-title')}>Paziņojumi</p>
            <p className="text-xs text-[#2E2E2E]/60">{unread ? `${unread} neizlasīti` : 'Visi izlasīti'}</p>
          </div>
          {unread > 0 && (
            <Button size="sm" variant="ghost" onClick={markAll} className="text-xs text-[#1F3D2B]" data-testid={tid('notification-mark-all')}>
              <CheckCheck className="w-4 h-4 mr-1" /> Atzīmēt visus
            </Button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto divide-y divide-gray-100 bg-white" data-testid={tid('notification-list')}>
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-[#2E2E2E]/60" data-testid={tid('notification-empty')}>
              <BellOff className="w-8 h-8 mx-auto mb-2 opacity-30" />
              Pagaidām nav paziņojumu
            </div>
          ) : items.map((item) => <NotificationItem key={item.id} item={item} onOpen={openItem} />)}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
